<!--
	SPDX-FileCopyrightText: Ferdinand Thiessen <opensource@fthiessen.de>
	SPDX-License-Identifier: AGPL-3.0-or-later
-->
<template>
	<NcIconSvgWrapper
		:class="iconWrapperClass"
		:path="svgPath"
		:name="severityName" />
</template>

<script setup lang="ts">
import type { ISeverity } from '../types.ts'

import { SEVERITY_NAMES } from '../constants.ts'
import {
	mdiAlert,
	mdiCheck,
	mdiExclamationThick,
	mdiHelp,
	mdiInformation,
} from '@mdi/js'
import { translate as t } from '@nextcloud/l10n'
import { computed, useCssModule } from 'vue'

import NcIconSvgWrapper from '@nextcloud/vue/dist/Components/NcIconSvgWrapper.js'

const props = defineProps<{
	severity: ISeverity
	/**
	 * Make the icon visual only and do not provide any accessible text
	 */
	visual?: boolean
}>()

/**
 * CSS classes (as we use CSS modules this is the mapping from readable names to the compiled class names)
 */
const style = useCssModule()

/**
 * MDI Icon to show for the level
 */
const svgPath = computed(() => {
	switch (props.severity) {
		case 'success':
			return mdiCheck
		case 'info':
			return mdiInformation
		case 'warning':
			return mdiAlert
		case 'error':
			return mdiExclamationThick
		default:
			return mdiHelp
	}
})

/**
 * Label to be used for accessibility on the icon
 */
const severityName = computed(() => {
	if (props.visual) {
		// visual only
		return ''
	}
	return t('security_guard', 'Severity: {severity}', {
		severity: SEVERITY_NAMES[props.severity] ?? SEVERITY_NAMES.unknown,
	})
})

/**
 * Class to assign to the icon wrapper
 * Used to set the background color
 */
const iconWrapperClass = computed(() => style[props.severity])
</script>

<style module>
.severity-icon {
	/* Set the color to the background color as this is ensured to have the needed 3:1 contrast for graphical elements */
	color: var(--color-main-background);
	/* Make the icons round */
	border-radius: 50%;
	/* TODO: Allow size parameter in NcIconSvgWrapper */
	max-height: 44px;
	max-width: 44px;
	min-width: 44px !important;
	min-height: 44px !important;
}

.success {
	composes: severity-icon;
	background-color: var(--color-success);
}

.info {
	composes: severity-icon;
	background-color: var(--color-info);
}

.warning {
	composes: severity-icon;
	background-color: var(--color-warning);
}

.error {
	composes: severity-icon;
	background-color: var(--color-error);
}
</style>
